import React, { FunctionComponent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useApi } from 'components/providers/ApiProvider'
import { useParkingLot } from 'components/views/parking-lot/ParkingLotProvider'
import { useMessage } from 'Elements/Message'
import useFormal from '@kevinwolf/formal-web'
import * as yup from 'yup'
import { formatTimestamp } from 'utils'
import { DraggableDialog, DraggableDialogTitle } from 'components/elements/DraggableDialog'
import DialogContent from '@material-ui/core/DialogContent'
import DialogActions from '@material-ui/core/DialogActions'
import Grid from '@material-ui/core/Grid'
import { TextField } from 'components/elements/TextField'
import Button from '@material-ui/core/Button'
import DialogLoader from 'Elements/DialogLoader'

export interface RegisterCashDeskPaymentDialogData {
  sessionId: number
}

type Props = {
  open: boolean
  data: RegisterCashDeskPaymentDialogData
}
type Functions = {
  setOpen(value: boolean): void
}
type AllProps = Props & Functions

const RegisterCashDeskPaymentDialog: FunctionComponent<AllProps> = ({ open, setOpen, data }) => {
  if (!data) {
    return null
  }

  const { t } = useTranslation()
  const api = useApi()
  const { setLoadAttachPaymentList } = useParkingLot()
  const { showSuccess, showError } = useMessage()

  const [request, setRequest] = useState<{
    amount: number
    until: string | null
  }>(null)

  const handleEnter = () => {
    api.requestParkingLotSessionPayment({
      session_id: data.sessionId,
    })
      .then(data => {
        formal.change('amount', (data.amount / 100).toFixed(2))
        setRequest(data)
      })
      .catch(() => {
      })
  }

  const handleClose = () => {
    setOpen(false)
  }

  const handleExited = () => {
    setRequest(null)
    formal.change('amount', initialValues.amount)
    formal.clearErrors()
  }

  const initialValues = {
    amount: 0,
  }

  const schema = yup.object().shape({
    amount: yup.number().required().moreThan(0).test({
      test: value => {
        return (Math.round(value * 100) / 100) == value
      },
    }),
  })

  const formal = useFormal(initialValues, {
    schema,
    onSubmit: async values => {
      values = schema.cast(values)

      await api.registerParkingLotSessionPayment({
        session_id: data.sessionId,
        amount: Math.round(values.amount * 100),
      })
        .then(data => {
          if (!data || !data.status) {
            throw new Error
          }

          handleClose()

          setLoadAttachPaymentList()

          showSuccess(t('messages.paymentRegistered'))
        })
        .catch(error => {
          let message

          switch (error.code) {
            case 3:
              message = t('alerts.checkInput')
              break
            default:
              message = t('alerts.tryLater')
          }

          showError(`${t('alerts.couldNotRegisterPayment')} ${message}`)
        })
    },
  })

  return (
    <DraggableDialog fullWidth open={open} onClose={handleClose} TransitionProps={{ onEnter: handleEnter, onExited: handleExited }}>
      <form {...formal.getFormProps()} noValidate>
        <DraggableDialogTitle>{t('registerCashDeskPayment')}</DraggableDialogTitle>
        <DialogContent>
          {!request && <DialogLoader />}

          {!!request && (
            <Grid container>
              <TextField
                GridProps={{ xs: 12 }}
                label={t('amount')}
                required
                {...formal.getFieldProps('amount')}
                autoComplete="off"
                autoFocus={true}
                InputProps={{
                  style: { fontSize: '1.2rem', fontWeight: 500 },
                }}
                helperText={request.until ? t('cashDeskPaymentAmountHelperText', { amount: (request.amount / 100).toFixed(2), until: formatTimestamp(request.until, 'dd.MM.yyyy HH:mm:ss') }) : undefined}
              />
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleClose}
          >
            {t('cancel')}
          </Button>
          {!!request && (
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={formal.isSubmitting}
            >
              {t('register')}
            </Button>
          )}
        </DialogActions>
      </form>
    </DraggableDialog>
  )
}

export default RegisterCashDeskPaymentDialog
